import { useCallback, useEffect, useState, useRef } from 'react';
import {
  IContractPromptProcess,
  useOrderStore,
} from '../../../../store/useOrder';

/**
 * @author taulmax
 * @date 2024.11.16
 * @description 주문 확정 상세 - 프롬프트 컴포넌트
 */
const OFDPrompt = ({ process }: { process: IContractPromptProcess }) => {
  const { updatePromptProcess, retryContractPrompt } = useOrderStore();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState<boolean>(false);
  const onClickOpen = useCallback(() => {
    setOpen(prev => {
      const newState = !prev;
      // 열릴 때 컨테이너가 보이도록 스크롤
      if (newState && containerRef.current) {
        setTimeout(() => {
          containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 100);
      }
      return newState;
    });
  }, []);

  // 초기값 세팅
  const initialData = process?.resultContent || '';
  const [result, setResult] = useState<string>(initialData);

  // 변경 감지
  const [isModified, setIsModified] = useState<boolean>(false);

  // 데이터 들어오면 다시 setState
  useEffect(() => {
    setResult(initialData);
    setIsModified(false);
  }, [initialData]);

  // textarea 높이 자동 조정
  useEffect(() => {
    if (textareaRef.current && open) {
      // 기본 높이로 설정
      textareaRef.current.style.height = '140px';
      // 컨텐츠 높이에 맞춰 조정
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = Math.min(scrollHeight, 400) + 'px';
    }
  }, [result, open]);

  const onChangeResult = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setResult(newValue);
      setIsModified(newValue !== initialData); // flag 설정
    },
    [initialData]
  );

  // 초기화 버튼 클릭
  const onClickReset = useCallback(() => {
    setResult(initialData);
    setIsModified(false);
  }, [initialData]);

  // 수정 버튼 클릭
  const onClickUpdate = useCallback(() => {
    const id = process.id;
    updatePromptProcess(id, result);
  }, [process.id, result, updatePromptProcess]);

  // 리트라이 버튼 클릭
  const onClickRetry = useCallback(() => {
    retryContractPrompt([process.id]);
  }, [process.id, retryContractPrompt]);

  const statusStyles = {
    SUCCESS: {
      bg: '!bg-green-50',
      border: '!border-green-200',
      badge: '!bg-green-100 !text-green-700',
      dot: '!bg-green-500',
    },
    COMPLETED: {
      bg: '!bg-green-50',
      border: '!border-green-200',
      badge: '!bg-green-100 !text-green-700',
      dot: '!bg-green-500',
    },
    FAIL: {
      bg: '!bg-red-50',
      border: '!border-red-200',
      badge: '!bg-red-100 !text-red-700',
      dot: '!bg-red-500',
    },
    FAILED: {
      bg: '!bg-red-50',
      border: '!border-red-200',
      badge: '!bg-red-100 !text-red-700',
      dot: '!bg-red-500',
    },
    EXPIRED: {
      bg: '!bg-red-50',
      border: '!border-red-200',
      badge: '!bg-red-100 !text-red-700',
      dot: '!bg-red-500',
    },
    CANCELLED: {
      bg: '!bg-red-50',
      border: '!border-red-200',
      badge: '!bg-red-100 !text-red-700',
      dot: '!bg-red-500',
    },
    PENDING: {
      bg: '!bg-yellow-50',
      border: '!border-yellow-200',
      badge: '!bg-yellow-100 !text-yellow-700',
      dot: '!bg-yellow-500',
    },
    IN_PROGRESS: {
      bg: '!bg-blue-50',
      border: '!border-blue-200',
      badge: '!bg-blue-100 !text-blue-700',
      dot: '!bg-blue-500',
    },
    VALIDATING: {
      bg: '!bg-blue-50',
      border: '!border-blue-200',
      badge: '!bg-blue-100 !text-blue-700',
      dot: '!bg-blue-500',
    },
    FINALIZING: {
      bg: '!bg-blue-50',
      border: '!border-blue-200',
      badge: '!bg-blue-100 !text-blue-700',
      dot: '!bg-blue-500',
    },
    BEFORE_BATCH: {
      bg: '!bg-yellow-50',
      border: '!border-yellow-200',
      badge: '!bg-yellow-100 !text-yellow-700',
      dot: '!bg-yellow-500',
    },
    BATCH_PROCESSING: {
      bg: '!bg-blue-50',
      border: '!border-blue-200',
      badge: '!bg-blue-100 !text-blue-700',
      dot: '!bg-blue-500',
    },
  }[process.status.code] || {
    bg: '!bg-gray-50',
    border: '!border-gray-200',
    badge: '!bg-gray-100 !text-gray-700',
    dot: '!bg-gray-500',
  };

  return (
    <div
      ref={containerRef}
      className={`!rounded-lg !border !transition-all !duration-200 hover:!shadow-md ${statusStyles.bg} ${statusStyles.border} !cursor-pointer !group`}
    >
      {/* 상단 영역 */}
      <div 
        className="!flex !items-center !justify-between !p-4"
        onClick={onClickOpen}
      >
        <div className="!flex !items-center !gap-3">
          {/* 상태 표시 점 */}
          <div className="!relative">
            <div className="!w-10 !h-10 !rounded-full !bg-white !shadow-sm !flex !items-center !justify-center">
              <div
                className={`!w-4 !h-4 !rounded-full ${statusStyles.dot} ${
                  process.status.code === 'IN_PROGRESS' || process.status.code === 'BATCH_PROCESSING' || process.status.code === 'VALIDATING' || process.status.code === 'FINALIZING' ? '!animate-pulse' : ''
                }`}
              ></div>
            </div>
            {(process.status.code === 'FAIL' || process.status.code === 'FAILED' || process.status.code === 'EXPIRED' || process.status.code === 'CANCELLED') && (
              <div className="!absolute !-top-1 !-right-1 !w-3 !h-3 !bg-red-500 !rounded-full !animate-ping"></div>
            )}
          </div>
          <div className="!flex-1">
            <div className="!flex !items-center !gap-2">
              <h4 className="!font-semibold !text-gray-900 !text-sm">
                {process.promptTitle}
              </h4>
              <span
                className={`!text-xs !font-medium !px-2 !py-0.5 !rounded-full ${statusStyles.badge}`}
              >
                {process.status.code === 'SUCCESS' || process.status.code === 'COMPLETED' ? '성공' : 
                 process.status.code === 'FAIL' || process.status.code === 'FAILED' || process.status.code === 'EXPIRED' || process.status.code === 'CANCELLED' ? '실패' : 
                 process.status.code === 'IN_PROGRESS' || process.status.code === 'BATCH_PROCESSING' || process.status.code === 'VALIDATING' || process.status.code === 'FINALIZING' ? '처리중' :
                 process.status.code === 'PENDING' || process.status.code === 'BEFORE_BATCH' ? '대기중' : process.status.code}
              </span>
            </div>
            <p className="!text-xs !text-gray-500 !mt-0.5">
              ID: {process.id.slice(0, 8).toUpperCase()}
            </p>
          </div>
        </div>
        <div className="!flex !items-center !gap-2">
          {/* 화살표 아이콘 */}
          <div className={`!transition-transform !duration-200 ${
            open ? '!rotate-180' : ''
          }`}>
            <svg className="!w-5 !h-5 !text-gray-400 group-hover:!text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          {(process.status.code === 'FAIL' || process.status.code === 'BATCH_PROCESSING' || process.status.code === 'RETRY_PROGRESSING') && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClickRetry();
              }}
              className={`!px-3 !py-1.5 !rounded-md 
              ${process.status.code === 'FAIL' ? '!bg-red-600 hover:!bg-red-700' :(process.status.code === 'BATCH_PROCESSING' ? '!bg-blue-600 hover:!bg-blue-700': '!bg-gray-600 hover:!bg-gray-700')} 
              !text-xs !font-medium !text-white !transition-colors !flex !items-center !gap-1`}
            >
              <svg className="!w-3 !h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
              </svg>
              재시도
            </button>
          )}
        </div>
      </div>

      {/* 확장 영역 */}
      {open && (
        <div className="!px-4 !pb-4 !border-t !border-gray-100">
          <div className="!mb-3">
            <div className="!flex !items-center !justify-between !mb-2">
              <h5 className="!text-sm !font-medium !text-gray-800">
                프롬프트 결과
              </h5>
              <span className="!text-xs !text-gray-500">
                {process.status.description}
              </span>
            </div>

            {process.validationData && (
              <p className="!text-xs !text-red-600 !mb-2 !p-2 !bg-red-50 !rounded !border !border-red-200">
                {process.validationData}
              </p>
            )}
          </div>

          <textarea
            ref={textareaRef}
            value={result}
            onChange={onChangeResult}
            className={`!w-full !min-h-[140px] !max-h-[400px] !px-3 !py-2 !border !rounded-md !text-sm !font-normal !text-gray-900 placeholder:!text-gray-400 placeholder:!text-sm placeholder:!font-normal !leading-relaxed !resize-none !overflow-y-auto focus:!outline-none focus:!border-blue-500 !transition-all !duration-200 ${
              process.status.code === 'FAIL'
                ? '!border-red-300 !bg-red-50'
                : '!border-gray-200 !bg-white'
            }`}
            style={{
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", "Noto Sans CJK KR", "Noto Sans CJK SC", "Noto Sans CJK TC", "Noto Sans CJK JP", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"'
            }}
            placeholder="프롬프트 결과가 표시됩니다..."
          />

          {isModified && (
            <div className="!flex !items-center !justify-between !mt-3 !p-2 !bg-amber-50 !rounded !border !border-amber-200">
              <span className="!text-xs !text-amber-700 !font-medium">
                변경사항이 있습니다
              </span>
              <div className="!flex !gap-2">
                <button
                  onClick={onClickReset}
                  className="!px-3 !py-1 !text-xs !bg-white !border !border-gray-300 hover:!bg-gray-50 !text-gray-700 !rounded !transition-colors"
                >
                  초기화
                </button>
                <button
                  onClick={onClickUpdate}
                  className="!px-3 !py-1 !text-xs !bg-blue-600 hover:!bg-blue-700 !text-white !rounded !transition-colors"
                >
                  저장
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OFDPrompt;
